import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Palette, FileText, Save, AlertCircle, Eye, EyeOff, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Theme Settings
  const [themeSettings, setThemeSettings] = useState({
    primary_color: '#1EA0E6',
    accent_color: '#7CD4FD',
    background_color: '#0B0F14',
    card_background: '#0F141A',
    border_color: '#1F2937',
    text_primary: '#E6EDF3',
    text_secondary: '#94A3B8',
    success_color: '#22C55E',
    warning_color: '#F59E0B',
    error_color: '#EF4444'
  });

  // Disclaimer Settings
  const [disclaimerSettings, setDisclaimerSettings] = useState({
    de: '',
    en: '',
    terms_url: '',
    privacy_url: ''
  });

  // Stripe Settings
  const [stripeSettings, setStripeSettings] = useState({
    stripe_api_key: '',
    stripe_publishable_key: '',
    webhook_secret: ''
  });

  // OpenAI Settings
  const [openaiSettings, setOpenaiSettings] = useState({
    openai_api_key: ''
  });

  // Tavily Settings
  const [tavilySettings, setTavilySettings] = useState({
    tavily_api_key: ''
  });

  // Currency/Geo-Pricing Settings
  const [currencySettings, setCurrencySettings] = useState({
    exchangerate_api_key: ''
  });

  // Contact Settings
  const [contactSettings, setContactSettings] = useState({
    support_email: '',
    support_name: 'Carcortex-ai Support'
  });

  // Show/Hide API Keys
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [showOpenAIKey, setShowOpenAIKey] = useState(false);
  const [showTavilyKey, setShowTavilyKey] = useState(false);
  const [showCurrencyKey, setShowCurrencyKey] = useState(false);
  
  // Test results
  const [stripeTestResult, setStripeTestResult] = useState(null);
  const [stripeTestLoading, setStripeTestLoading] = useState(false);
  const [tavilyTestResult, setTavilyTestResult] = useState(null);
  const [testingTavily, setTestingTavily] = useState(false);
  const [currencyTestResult, setCurrencyTestResult] = useState(null);
  const [testingCurrency, setTestingCurrency] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch theme and disclaimer settings
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.theme) {
        setThemeSettings(response.data.theme);
      }
      if (response.data.disclaimer) {
        setDisclaimerSettings({
          de: response.data.disclaimer.de || '',
          en: response.data.disclaimer.en || '',
          terms_url: response.data.disclaimer.terms_url || '',
          privacy_url: response.data.disclaimer.privacy_url || ''
        });
      }
      if (response.data.stripe) {
        setStripeSettings({
          stripe_api_key: response.data.stripe.stripe_api_key || '',
          stripe_publishable_key: response.data.stripe.stripe_publishable_key || '',
          webhook_secret: response.data.stripe.webhook_secret || ''
        });
      }
      if (response.data.tavily) {
        setTavilySettings({
          tavily_api_key: response.data.tavily.tavily_api_key || ''
        });
      }
      if (response.data.openai) {
        setOpenaiSettings(response.data.openai);
      }
      if (response.data.contact) {
        setContactSettings(response.data.contact);
      }
      
      // Fetch currency settings separately
      try {
        const currencyResponse = await axios.get(`${API}/admin/settings/currency`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (currencyResponse.data.api_key_masked) {
          // Don't overwrite with masked key
        }
      } catch (e) {
        console.log('Currency settings not configured yet');
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTheme = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/settings/theme`, themeSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Theme-Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern der Theme-Einstellungen');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDisclaimer = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/settings/disclaimer`, disclaimerSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Disclaimer-Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern der Disclaimer-Einstellungen');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStripe = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/settings/stripe`, stripeSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Stripe-Einstellungen gespeichert');
      // Reset test result after saving
      setStripeTestResult(null);
    } catch (error) {
      toast.error('Fehler beim Speichern der Stripe-Einstellungen');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestStripe = async () => {
    setStripeTestLoading(true);
    setStripeTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/settings/stripe/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeTestResult(response.data);
      
      if (response.data.overall_status === 'OK') {
        toast.success('Stripe-Konfiguration erfolgreich getestet!');
      } else if (response.data.overall_status === 'WARNUNG') {
        toast.warning('Stripe funktioniert, aber mit Warnungen');
      } else {
        toast.error('Stripe-Konfiguration fehlerhaft');
      }
    } catch (error) {
      toast.error('Fehler beim Testen der Stripe-Konfiguration');
      console.error(error);
      setStripeTestResult({
        overall_status: 'FEHLER',
        errors: [error.response?.data?.detail || 'Verbindungsfehler']
      });
    } finally {
      setStripeTestLoading(false);
    }
  };

  const handleSaveOpenAI = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/settings/openai`, openaiSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('OpenAI-Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern der OpenAI-Einstellungen');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTavily = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/settings/tavily`, tavilySettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tavily-Einstellungen gespeichert');
      // Reset test result after saving new key
      setTavilyTestResult(null);
    } catch (error) {
      toast.error('Fehler beim Speichern der Tavily-Einstellungen');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestTavily = async () => {
    setTestingTavily(true);
    setTavilyTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/settings/tavily/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTavilyTestResult(response.data);
      if (response.data.success) {
        toast.success('Tavily funktioniert korrekt!');
      } else {
        toast.error(response.data.error || 'Tavily-Test fehlgeschlagen');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Tavily-Test fehlgeschlagen';
      setTavilyTestResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingTavily(false);
    }
  };

  const handleSaveCurrency = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/settings/currency`, currencySettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Währungs-Einstellungen gespeichert');
      setCurrencyTestResult(null);
    } catch (error) {
      toast.error('Fehler beim Speichern der Währungs-Einstellungen');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleTestCurrency = async () => {
    setTestingCurrency(true);
    setCurrencyTestResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/settings/currency/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrencyTestResult(response.data);
      if (response.data.success) {
        toast.success('ExchangeRate API funktioniert!');
      } else {
        toast.error(response.data.error || 'Test fehlgeschlagen');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Test fehlgeschlagen';
      setCurrencyTestResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTestingCurrency(false);
    }
  };

  const handleThemeChange = (field, value) => {
    setThemeSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleDisclaimerChange = (field, value) => {
    setDisclaimerSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
        <div className="flex items-center justify-center h-64">
          <p style={{ color: '#94A3B8' }}>Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" style={{ backgroundColor: '#0B0F14' }}>
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl font-bold mb-8" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
          System-Einstellungen
        </h2>

        {/* Theme Settings */}
        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <Palette className="w-5 h-5" style={{ color: '#7CD4FD' }} />
              Theme-Einstellungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Primary Colors */}
              <div>
                <h4 className="font-semibold mb-4" style={{ color: '#E6EDF3' }}>Hauptfarben</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                      Primärfarbe
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={themeSettings.primary_color}
                        onChange={(e) => handleThemeChange('primary_color', e.target.value)}
                        className="w-20 h-10"
                      />
                      <Input
                        type="text"
                        value={themeSettings.primary_color}
                        onChange={(e) => handleThemeChange('primary_color', e.target.value)}
                        className="flex-1"
                        style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                      Akzentfarbe
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={themeSettings.accent_color}
                        onChange={(e) => handleThemeChange('accent_color', e.target.value)}
                        className="w-20 h-10"
                      />
                      <Input
                        type="text"
                        value={themeSettings.accent_color}
                        onChange={(e) => handleThemeChange('accent_color', e.target.value)}
                        className="flex-1"
                        style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Background Colors */}
              <div>
                <h4 className="font-semibold mb-4" style={{ color: '#E6EDF3' }}>Hintergrundfarben</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                      Haupthintergrund
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={themeSettings.background_color}
                        onChange={(e) => handleThemeChange('background_color', e.target.value)}
                        className="w-20 h-10"
                      />
                      <Input
                        type="text"
                        value={themeSettings.background_color}
                        onChange={(e) => handleThemeChange('background_color', e.target.value)}
                        className="flex-1"
                        style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                      Kartenhintergrund
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={themeSettings.card_background}
                        onChange={(e) => handleThemeChange('card_background', e.target.value)}
                        className="w-20 h-10"
                      />
                      <Input
                        type="text"
                        value={themeSettings.card_background}
                        onChange={(e) => handleThemeChange('card_background', e.target.value)}
                        className="flex-1"
                        style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Colors */}
              <div>
                <h4 className="font-semibold mb-4" style={{ color: '#E6EDF3' }}>Statusfarben</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                      Erfolg
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={themeSettings.success_color}
                        onChange={(e) => handleThemeChange('success_color', e.target.value)}
                        className="w-20 h-10"
                      />
                      <Input
                        type="text"
                        value={themeSettings.success_color}
                        onChange={(e) => handleThemeChange('success_color', e.target.value)}
                        className="flex-1"
                        style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                      Warnung
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={themeSettings.warning_color}
                        onChange={(e) => handleThemeChange('warning_color', e.target.value)}
                        className="w-20 h-10"
                      />
                      <Input
                        type="text"
                        value={themeSettings.warning_color}
                        onChange={(e) => handleThemeChange('warning_color', e.target.value)}
                        className="flex-1"
                        style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block mb-2 text-sm" style={{ color: '#94A3B8' }}>
                      Fehler
                    </label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={themeSettings.error_color}
                        onChange={(e) => handleThemeChange('error_color', e.target.value)}
                        className="w-20 h-10"
                      />
                      <Input
                        type="text"
                        value={themeSettings.error_color}
                        onChange={(e) => handleThemeChange('error_color', e.target.value)}
                        className="flex-1"
                        style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleSaveTheme}
                disabled={saving}
                style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Speichern...' : 'Theme speichern'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer Settings */}
        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <FileText className="w-5 h-5" style={{ color: '#7CD4FD' }} />
              Disclaimer & Rechtliche Hinweise
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="p-4 rounded flex items-start gap-3" style={{ backgroundColor: '#F59E0B20', borderLeft: '4px solid #F59E0B' }}>
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#F59E0B' }} />
                <div>
                  <p className="font-semibold mb-1" style={{ color: '#F59E0B' }}>Wichtiger Hinweis</p>
                  <p className="text-sm" style={{ color: '#E6EDF3' }}>
                    Diese Texte werden Benutzern vor der Diagnose angezeigt. Stellen Sie sicher, dass alle rechtlichen Anforderungen erfüllt sind.
                  </p>
                </div>
              </div>

              <div>
                <label className="block mb-2 font-semibold" style={{ color: '#E6EDF3' }}>
                  Disclaimer (Deutsch)
                </label>
                <textarea
                  rows={6}
                  value={disclaimerSettings.de}
                  onChange={(e) => handleDisclaimerChange('de', e.target.value)}
                  placeholder="Geben Sie den deutschen Disclaimer-Text ein..."
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3', padding: '8px', borderRadius: '6px', width: '100%', border: '1px solid #1F2937' }}
                />
              </div>

              <div>
                <label className="block mb-2 font-semibold" style={{ color: '#E6EDF3' }}>
                  Disclaimer (English)
                </label>
                <textarea
                  rows={6}
                  value={disclaimerSettings.en}
                  onChange={(e) => handleDisclaimerChange('en', e.target.value)}
                  placeholder="Enter the English disclaimer text..."
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3', padding: '8px', borderRadius: '6px', width: '100%', border: '1px solid #1F2937' }}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2" style={{ color: '#94A3B8' }}>
                    AGB URL
                  </label>
                  <Input
                    type="url"
                    value={disclaimerSettings.terms_url}
                    onChange={(e) => handleDisclaimerChange('terms_url', e.target.value)}
                    placeholder="https://..."
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>

                <div>
                  <label className="block mb-2" style={{ color: '#94A3B8' }}>
                    Datenschutz URL
                  </label>
                  <Input
                    type="url"
                    value={disclaimerSettings.privacy_url}
                    onChange={(e) => handleDisclaimerChange('privacy_url', e.target.value)}
                    placeholder="https://..."
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveDisclaimer}
                  disabled={saving}
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Speichern...' : 'Disclaimer speichern'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stripe Settings */}
        <Card className="mt-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <FileText className="w-5 h-5" style={{ color: '#7CD4FD' }} />
              Stripe Zahlungseinstellungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="p-4 rounded flex items-start gap-3" style={{ backgroundColor: '#1EA0E620', borderLeft: '4px solid #1EA0E6' }}>
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#1EA0E6' }} />
                <div>
                  <p className="font-semibold mb-1" style={{ color: '#1EA0E6' }}>Wichtig</p>
                  <p className="text-sm" style={{ color: '#E6EDF3' }}>
                    Diese Einstellungen werden für alle Zahlungsvorgänge verwendet. Verwenden Sie Test-Keys für Entwicklung und Live-Keys für Produktion.
                  </p>
                </div>
              </div>

              <div>
                <label className="block mb-2 font-semibold" style={{ color: '#E6EDF3' }}>
                  Stripe Secret Key (sk_...)
                </label>
                <div className="flex gap-2">
                  <Input
                    type={showStripeKey ? "text" : "password"}
                    value={stripeSettings.stripe_api_key}
                    onChange={(e) => setStripeSettings({ ...stripeSettings, stripe_api_key: e.target.value })}
                    placeholder="sk_test_... oder sk_live_..."
                    className="flex-1"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowStripeKey(!showStripeKey)}
                    style={{ borderColor: '#1F2937', color: '#94A3B8' }}
                  >
                    {showStripeKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                  Wird für Server-seitige API-Aufrufe verwendet
                </p>
              </div>

              <div>
                <label className="block mb-2 font-semibold" style={{ color: '#E6EDF3' }}>
                  Stripe Publishable Key (pk_...)
                </label>
                <Input
                  type="text"
                  value={stripeSettings.stripe_publishable_key}
                  onChange={(e) => setStripeSettings({ ...stripeSettings, stripe_publishable_key: e.target.value })}
                  placeholder="pk_test_... oder pk_live_..."
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                  Wird für Client-seitige Anfragen verwendet
                </p>
              </div>

              <div>
                <label className="block mb-2 font-semibold" style={{ color: '#E6EDF3' }}>
                  Webhook Secret (whsec_...)
                </label>
                <div className="flex gap-2">
                  <Input
                    type={showWebhookSecret ? "text" : "password"}
                    value={stripeSettings.webhook_secret}
                    onChange={(e) => setStripeSettings({ ...stripeSettings, webhook_secret: e.target.value })}
                    placeholder="whsec_..."
                    className="flex-1"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                    style={{ borderColor: '#1F2937', color: '#94A3B8' }}
                  >
                    {showWebhookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                  Webhook-Signing-Secret für sichere Webhook-Verifikation. Zu finden unter: Stripe Dashboard → Developers → Webhooks → Ihr Endpoint → Signing secret
                </p>
              </div>

              {/* Webhook Setup Instructions */}
              <div className="p-4 rounded-lg border-2" style={{ backgroundColor: '#1E293B', borderColor: '#F59E0B' }}>
                <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: '#F59E0B' }}>
                  <AlertCircle className="w-5 h-5" />
                  Webhook-Konfiguration (Stripe Dashboard)
                </h4>
                <p className="text-sm mb-3" style={{ color: '#94A3B8' }}>
                  Für automatische Zahlungsverarbeitung müssen Sie im Stripe Dashboard einen Webhook einrichten:
                </p>
                <div className="space-y-2 text-sm" style={{ color: '#E6EDF3' }}>
                  <p><strong>1. Endpoint URL (kopieren Sie diese URL):</strong></p>
                  <div className="flex items-center gap-2">
                    <input 
                      type="text"
                      readOnly
                      value={window.location.hostname.includes('preview.emergentagent.com') 
                        ? `${window.location.origin}/api/webhook/stripe` 
                        : 'https://diag-portal.emergent.host/api/webhook/stripe'}
                      className="flex-1 p-2 rounded text-xs font-mono"
                      style={{ backgroundColor: '#0B0F14', color: '#7CD4FD', border: '1px solid #1F2937' }}
                      onClick={(e) => {
                        e.target.select();
                        navigator.clipboard.writeText(e.target.value);
                        toast.success('Webhook-URL kopiert!');
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const webhookUrl = window.location.hostname.includes('preview.emergentagent.com') 
                          ? `${window.location.origin}/api/webhook/stripe` 
                          : 'https://diag-portal.emergent.host/api/webhook/stripe';
                        navigator.clipboard.writeText(webhookUrl);
                        toast.success('Webhook-URL kopiert!');
                      }}
                      className="shrink-0"
                    >
                      Kopieren
                    </Button>
                  </div>
                  {window.location.hostname.includes('preview.emergentagent.com') && (
                    <p className="text-xs mt-1" style={{ color: '#F59E0B' }}>
                      ⚠️ Sie befinden sich in der Preview-Umgebung. Für Produktion verwenden Sie: 
                      <code className="ml-1" style={{ color: '#7CD4FD' }}>https://diag-portal.emergent.host/api/webhook/stripe</code>
                    </p>
                  )}
                  <p className="mt-3"><strong>2. Benötigte Events aktivieren:</strong></p>
                  <ul className="list-disc list-inside space-y-1" style={{ color: '#94A3B8' }}>
                    <li><code style={{ color: '#22C55E' }}>checkout.session.completed</code> - Neukauf abgeschlossen</li>
                    <li><code style={{ color: '#22C55E' }}>invoice.payment_succeeded</code> - Monatliche Zahlung erfolgreich</li>
                    <li><code style={{ color: '#F59E0B' }}>invoice.payment_failed</code> - Zahlung fehlgeschlagen (Mahnung)</li>
                    <li><code style={{ color: '#EF4444' }}>customer.subscription.deleted</code> - Abo gelöscht/abgelaufen</li>
                  </ul>
                  <p className="mt-3 text-xs" style={{ color: '#94A3B8' }}>
                    Nach dem Erstellen: Kopieren Sie das "Signing secret" (whsec_...) in das Feld oben.
                  </p>
                </div>
              </div>

              {/* Stripe Test Results */}
              {stripeTestResult && (
                <div className="p-4 rounded-lg" style={{ 
                  backgroundColor: stripeTestResult.overall_status === 'OK' ? 'rgba(34,197,94,0.1)' : 
                                   stripeTestResult.overall_status === 'WARNUNG' ? 'rgba(245,158,11,0.1)' : 
                                   'rgba(239,68,68,0.1)',
                  borderColor: stripeTestResult.overall_status === 'OK' ? '#22C55E' : 
                               stripeTestResult.overall_status === 'WARNUNG' ? '#F59E0B' : 
                               '#EF4444',
                  border: '1px solid'
                }}>
                  <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ 
                    color: stripeTestResult.overall_status === 'OK' ? '#22C55E' : 
                           stripeTestResult.overall_status === 'WARNUNG' ? '#F59E0B' : 
                           '#EF4444'
                  }}>
                    {stripeTestResult.overall_status === 'OK' ? '✅' : 
                     stripeTestResult.overall_status === 'WARNUNG' ? '⚠️' : '❌'} 
                    Stripe Konfigurationstest: {stripeTestResult.overall_status}
                  </h4>
                  
                  <div className="space-y-2 text-sm">
                    {/* Status Checks */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center gap-2">
                        {stripeTestResult.api_key_valid ? '✅' : '❌'}
                        <span style={{ color: '#E6EDF3' }}>API-Key gültig</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {stripeTestResult.connection_ok ? '✅' : '❌'}
                        <span style={{ color: '#E6EDF3' }}>Verbindung OK</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {stripeTestResult.webhook_secret_configured ? '✅' : '⚠️'}
                        <span style={{ color: '#E6EDF3' }}>Webhook-Secret konfiguriert</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {stripeTestResult.webhook_events_ok === true ? '✅' : stripeTestResult.webhook_events_ok === false ? '❌' : '⚠️'}
                        <span style={{ color: '#E6EDF3' }}>Webhook-Events konfiguriert</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {stripeTestResult.products_found > 0 ? '✅' : '⚠️'}
                        <span style={{ color: '#E6EDF3' }}>{stripeTestResult.products_found} Produkte gefunden</span>
                      </div>
                    </div>
                    
                    {/* Mode Info */}
                    {stripeTestResult.details?.mode && (
                      <div className="mt-2 p-2 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                        <span style={{ color: '#94A3B8' }}>Modus: </span>
                        <span style={{ 
                          color: stripeTestResult.details.mode === 'LIVE' ? '#22C55E' : '#F59E0B',
                          fontWeight: 'bold'
                        }}>
                          {stripeTestResult.details.mode}
                        </span>
                        {stripeTestResult.details.balance && (
                          <span style={{ color: '#94A3B8', marginLeft: '16px' }}>
                            Guthaben: {stripeTestResult.details.balance.available} {stripeTestResult.details.balance.currency}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {/* Webhook Events Check */}
                    {stripeTestResult.webhook_events && (
                      <div className="mt-3 p-3 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                        <p className="font-semibold mb-2 flex items-center gap-2" style={{ color: '#E6EDF3' }}>
                          {stripeTestResult.webhook_events_ok ? '✅' : stripeTestResult.webhook_events_ok === false ? '❌' : '⚠️'}
                          Webhook-Events ({stripeTestResult.webhooks_found || 0} Endpoint{stripeTestResult.webhooks_found !== 1 ? 's' : ''})
                        </p>
                        
                        {/* Configured Events */}
                        {stripeTestResult.webhook_events.configured?.length > 0 && (
                          <div className="mb-2">
                            <span style={{ color: '#22C55E', fontSize: '12px' }}>✓ Konfiguriert:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {stripeTestResult.webhook_events.configured.map((event, i) => (
                                <code key={i} className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: 'rgba(34,197,94,0.2)', color: '#22C55E' }}>
                                  {event}
                                </code>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Missing Events */}
                        {stripeTestResult.webhook_events.missing?.length > 0 && (
                          <div>
                            <span style={{ color: '#EF4444', fontSize: '12px' }}>✗ Fehlend:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {stripeTestResult.webhook_events.missing.map((event, i) => (
                                <code key={i} className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: 'rgba(239,68,68,0.2)', color: '#EF4444' }}>
                                  {event}
                                </code>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Endpoint URLs - Show only relevant endpoints */}
                        {stripeTestResult.webhook_events.relevant_endpoints?.length > 0 ? (
                          <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <span style={{ color: '#94A3B8', fontSize: '11px' }}>✅ Aktive Endpoints für diag-portal:</span>
                            {stripeTestResult.webhook_events.relevant_endpoints.map((ep, i) => (
                              <div key={i} className="text-xs mt-1 flex items-center gap-2">
                                <span style={{ color: ep.status === 'enabled' ? '#22C55E' : '#F59E0B' }}>
                                  {ep.status === 'enabled' ? '●' : '○'}
                                </span>
                                <code style={{ color: '#7CD4FD', fontSize: '10px' }}>{ep.url}</code>
                                <span style={{ color: '#94A3B8', fontSize: '9px' }}>
                                  ({ep.enabled_events?.length || 0} Events)
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : stripeTestResult.webhook_events.endpoints?.length > 0 ? (
                          <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <span style={{ color: '#F59E0B', fontSize: '11px' }}>⚠️ Keine Endpoints für diag-portal gefunden. Vorhandene Endpoints:</span>
                            {stripeTestResult.webhook_events.endpoints.slice(0, 3).map((ep, i) => (
                              <div key={i} className="text-xs mt-1 flex items-center gap-2">
                                <span style={{ color: ep.status === 'enabled' ? '#94A3B8' : '#F59E0B' }}>
                                  {ep.status === 'enabled' ? '○' : '○'}
                                </span>
                                <code style={{ color: '#94A3B8', fontSize: '10px' }}>{ep.url}</code>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    )}
                    
                    {/* Errors */}
                    {stripeTestResult.errors?.length > 0 && (
                      <div className="mt-2">
                        <p className="font-semibold" style={{ color: '#EF4444' }}>Fehler:</p>
                        <ul className="list-disc list-inside" style={{ color: '#FCA5A5' }}>
                          {stripeTestResult.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Warnings */}
                    {stripeTestResult.warnings?.length > 0 && (
                      <div className="mt-2">
                        <p className="font-semibold" style={{ color: '#F59E0B' }}>Warnungen:</p>
                        <ul className="list-disc list-inside" style={{ color: '#FCD34D' }}>
                          {stripeTestResult.warnings.map((warn, i) => (
                            <li key={i}>{warn}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button
                  onClick={handleTestStripe}
                  disabled={stripeTestLoading}
                  variant="outline"
                  style={{ borderColor: '#1F2937', color: '#E6EDF3' }}
                >
                  {stripeTestLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Teste...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Konfiguration testen
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleSaveStripe}
                  disabled={saving}
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Speichern...' : 'Stripe-Einstellungen speichern'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* OpenAI Settings for Dual-AI System */}
        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <AlertCircle className="w-5 h-5" style={{ color: '#7CD4FD' }} />
              OpenAI API Konfiguration (Dual-AI-System)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#1E293B', borderColor: '#1F2937' }}>
                <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>
                  <strong style={{ color: '#7CD4FD' }}>Dual-AI-System aktiviert:</strong> Wenn Sie hier einen OpenAI API Key hinterlegen, 
                  nutzt die Plattform sowohl Gemini 2.5 Pro als auch GPT-4o für Diagnosen. Die Ergebnisse werden 
                  gegenseitig validiert, was die Diagnosequalität deutlich erhöht und vage Antworten verhindert.
                </p>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  • <strong>Ohne OpenAI Key:</strong> Nur Gemini 2.5 Pro (Standard)<br/>
                  • <strong>Mit OpenAI Key:</strong> Gemini 2.5 Pro + GPT-4o (Konsensus-Validierung)
                </p>
              </div>

              <div>
                <label className="block mb-2 font-semibold" style={{ color: '#E6EDF3' }}>
                  OpenAI API Key (sk-proj-...)
                </label>
                <div className="flex gap-2">
                  <Input
                    type={showOpenAIKey ? "text" : "password"}
                    value={openaiSettings.openai_api_key}
                    onChange={(e) => setOpenaiSettings({ ...openaiSettings, openai_api_key: e.target.value })}
                    placeholder="sk-proj-..."
                    className="flex-1"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowOpenAIKey(!showOpenAIKey)}
                    style={{ borderColor: '#1F2937', color: '#94A3B8' }}
                  >
                    {showOpenAIKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                  Optional: Für Dual-AI-System mit höherer Diagnosequalität. 
                  Key erhalten Sie unter <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" style={{ color: '#7CD4FD' }}>platform.openai.com</a>
                </p>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleSaveOpenAI}
                  disabled={saving}
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Speichern...' : 'OpenAI-Einstellungen speichern'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tavily Settings for Web Research */}
        <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <AlertCircle className="w-5 h-5" style={{ color: '#7CD4FD' }} />
              Tavily API Konfiguration (Web-Recherche)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#1E293B', borderColor: '#1F2937' }}>
                <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>
                  <strong style={{ color: '#7CD4FD' }}>Live Web-Recherche:</strong> Mit einem Tavily API Key kann die Plattform 
                  in Echtzeit das Internet durchsuchen und aktuelle technische Informationen finden.
                </p>
                <p className="text-sm mb-2" style={{ color: '#94A3B8' }}>
                  <strong>Wo wird Tavily verwendet?</strong>
                </p>
                <ul className="text-sm space-y-1" style={{ color: '#94A3B8' }}>
                  <li>• <strong>Diagnose:</strong> Sucht nach ähnlichen Fehlern im Internet</li>
                  <li>• <strong>Fahrzeugdaten-Sammlung:</strong> Findet technische Spezifikationen</li>
                  <li>• <strong>Reparatur-Anleitungen:</strong> Sucht nach Anleitungen</li>
                </ul>
                <p className="text-sm mt-4" style={{ color: '#F59E0B' }}>
                  ⚠️ <strong>Optional:</strong> Ohne Tavily funktioniert die App normal, nutzt aber nur die interne Datenbank.
                </p>
              </div>

              <div>
                <label className="block mb-2 font-semibold" style={{ color: '#E6EDF3' }}>
                  Tavily API Key
                </label>
                <div className="flex gap-2">
                  <Input
                    type={showTavilyKey ? "text" : "password"}
                    value={tavilySettings.tavily_api_key}
                    onChange={(e) => setTavilySettings({ ...tavilySettings, tavily_api_key: e.target.value })}
                    placeholder="tvly-..."
                    className="flex-1"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowTavilyKey(!showTavilyKey)}
                    style={{ borderColor: '#1F2937', color: '#94A3B8' }}
                  >
                    {showTavilyKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                  Optional: Für Live-Web-Recherche. 
                  Key erhalten Sie unter <a href="https://tavily.com" target="_blank" rel="noopener noreferrer" style={{ color: '#7CD4FD' }}>tavily.com</a> (kostenloser Plan verfügbar)
                </p>
              </div>

              {/* Test Result */}
              {tavilyTestResult && (
                <div 
                  className="p-4 rounded-lg flex items-center gap-3"
                  style={{ 
                    backgroundColor: tavilyTestResult.success ? '#22C55E20' : '#EF444420',
                    borderLeft: `4px solid ${tavilyTestResult.success ? '#22C55E' : '#EF4444'}`
                  }}
                >
                  {tavilyTestResult.success ? (
                    <CheckCircle className="w-5 h-5" style={{ color: '#22C55E' }} />
                  ) : (
                    <XCircle className="w-5 h-5" style={{ color: '#EF4444' }} />
                  )}
                  <div>
                    <p className="font-semibold" style={{ color: tavilyTestResult.success ? '#22C55E' : '#EF4444' }}>
                      {tavilyTestResult.success ? 'Tavily funktioniert!' : 'Tavily-Test fehlgeschlagen'}
                    </p>
                    {tavilyTestResult.success && tavilyTestResult.results_count !== undefined && (
                      <p className="text-sm" style={{ color: '#94A3B8' }}>
                        Test-Suche ergab {tavilyTestResult.results_count} Ergebnisse
                      </p>
                    )}
                    {tavilyTestResult.error && (
                      <p className="text-sm" style={{ color: '#EF4444' }}>{tavilyTestResult.error}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button
                  onClick={handleTestTavily}
                  disabled={testingTavily || !tavilySettings.tavily_api_key}
                  variant="outline"
                  style={{ borderColor: '#1F2937', color: '#7CD4FD' }}
                >
                  {testingTavily ? 'Teste...' : 'Verbindung testen'}
                </Button>
                <Button
                  onClick={handleSaveTavily}
                  disabled={saving}
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Speichern...' : 'Tavily-Einstellungen speichern'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Currency/Geo-Pricing Settings */}
        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <span style={{ fontSize: '1.2rem' }}>💱</span>
              Währung & Geo-Pricing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#1E293B', borderColor: '#1F2937' }}>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  <strong style={{ color: '#7CD4FD' }}>Geo-Pricing:</strong> Preise werden automatisch in die Währung des Besuchers umgerechnet (EUR, CHF, USD).
                  <br />
                  <strong style={{ color: '#F59E0B' }}>Glättung:</strong> Preise werden auf .90 gerundet (z.B. 99.90 CHF) für ein professionelles Erscheinungsbild.
                </p>
              </div>

              <div>
                <label className="block mb-2 font-semibold" style={{ color: '#E6EDF3' }}>
                  ExchangeRate API Key
                </label>
                <div className="flex gap-2">
                  <Input
                    type={showCurrencyKey ? "text" : "password"}
                    value={currencySettings.exchangerate_api_key}
                    onChange={(e) => setCurrencySettings({ ...currencySettings, exchangerate_api_key: e.target.value })}
                    placeholder="Ihr API Key..."
                    className="flex-1"
                    style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCurrencyKey(!showCurrencyKey)}
                    style={{ borderColor: '#1F2937', color: '#94A3B8' }}
                  >
                    {showCurrencyKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                  Optional: Für Live-Wechselkurse. Kostenlos unter{' '}
                  <a href="https://www.exchangerate-api.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#7CD4FD' }}>
                    exchangerate-api.com
                  </a>{' '}
                  (1500 Anfragen/Monat kostenlos). Ohne Key werden Fallback-Kurse verwendet.
                </p>
              </div>

              {/* Currency Test Result */}
              {currencyTestResult && (
                <div 
                  className="p-4 rounded-lg flex items-start gap-3"
                  style={{ 
                    backgroundColor: currencyTestResult.success ? '#22C55E20' : '#EF444420',
                    borderLeft: `4px solid ${currencyTestResult.success ? '#22C55E' : '#EF4444'}`
                  }}
                >
                  {currencyTestResult.success ? (
                    <CheckCircle className="w-5 h-5 mt-0.5" style={{ color: '#22C55E' }} />
                  ) : (
                    <XCircle className="w-5 h-5 mt-0.5" style={{ color: '#EF4444' }} />
                  )}
                  <div>
                    <p className="font-semibold" style={{ color: currencyTestResult.success ? '#22C55E' : '#EF4444' }}>
                      {currencyTestResult.success ? 'ExchangeRate API funktioniert!' : 'Test fehlgeschlagen'}
                    </p>
                    {currencyTestResult.success && currencyTestResult.rates && (
                      <div className="text-sm mt-1" style={{ color: '#94A3B8' }}>
                        <p>EUR → CHF: {currencyTestResult.rates.CHF?.toFixed(4)}</p>
                        <p>EUR → USD: {currencyTestResult.rates.USD?.toFixed(4)}</p>
                        <p className="text-xs mt-1">API: {currencyTestResult.api_type}</p>
                      </div>
                    )}
                    {currencyTestResult.error && (
                      <p className="text-sm mt-1" style={{ color: '#EF4444' }}>{currencyTestResult.error}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button
                  onClick={handleTestCurrency}
                  disabled={testingCurrency}
                  variant="outline"
                  style={{ borderColor: '#1F2937', color: '#7CD4FD' }}
                >
                  {testingCurrency ? 'Teste...' : 'Verbindung testen'}
                </Button>
                <Button
                  onClick={handleSaveCurrency}
                  disabled={saving}
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Speichern...' : 'Währungs-Einstellungen speichern'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Settings */}
        <Card className="mb-6" style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ color: '#E6EDF3' }}>
              <FileText className="w-5 h-5" style={{ color: '#7CD4FD' }} />
              Kontakt-Einstellungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#1E293B', borderColor: '#1F2937' }}>
                <p className="text-sm" style={{ color: '#94A3B8' }}>
                  <strong style={{ color: '#7CD4FD' }}>Support-Email:</strong> Nachrichten vom Kontaktformular werden an diese E-Mail-Adresse gesendet.
                </p>
              </div>

              <div>
                <label className="block mb-2 font-semibold" style={{ color: '#E6EDF3' }}>
                  Support E-Mail-Adresse
                </label>
                <Input
                  type="email"
                  value={contactSettings.support_email}
                  onChange={(e) => setContactSettings({ ...contactSettings, support_email: e.target.value })}
                  placeholder="support@carcortex-ai.com"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                  Hier werden alle Anfragen aus dem Kontaktformular empfangen
                </p>
              </div>

              <div>
                <label className="block mb-2 font-semibold" style={{ color: '#E6EDF3' }}>
                  Support Name
                </label>
                <Input
                  type="text"
                  value={contactSettings.support_name}
                  onChange={(e) => setContactSettings({ ...contactSettings, support_name: e.target.value })}
                  placeholder="Carcortex-ai Support"
                  style={{ backgroundColor: '#0B0F14', borderColor: '#1F2937', color: '#E6EDF3' }}
                />
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>
                  Name des Absenders in den E-Mails
                </p>
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const token = localStorage.getItem('token');
                      await axios.post(`${API}/admin/settings/contact`, contactSettings, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      toast.success('Kontakt-Einstellungen gespeichert');
                    } catch (error) {
                      toast.error('Fehler beim Speichern');
                    } finally {
                      setSaving(false);
                    }
                  }}
                  disabled={saving}
                  style={{ backgroundColor: '#1EA0E6', color: '#ffffff' }}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Speichern...' : 'Kontakt-Einstellungen speichern'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
